import React, { useEffect, useState } from 'react';
import { useNavStep } from '../lib/NavHistory';
import {
  ClipboardList, Calendar, Stethoscope, Plus, Edit3, Save,
  Lock, ChevronLeft, Check, AlertTriangle, UserPlus, X, User, UserRoundPlus, FileText, AlertCircle,
} from 'lucide-react';
import PrintButton from '../components/PrintButton';
import PatientDropdown from '../components/PatientDropdown';

// ─── New Patient Quick-Register Modal ────────────────────────────────────────

const GENDER_OPTIONS = [
  { value: 'U', label: 'Unknown' },
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'O', label: 'Other' },
];

const US_STATES = [
  '','AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN',
  'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
  'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
  'VT','VA','WA','WV','WI','WY','DC',
];

const EMPTY_CLIENT = {
  firstName: '', lastName: '', dateOfBirth: '', gender: 'U',
  phone: '', email: '', address: '', city: '', state: '', zip: '',
  insuranceProvider: '', insuranceId: '', primaryPhysicianId: '',
};

function NewClientModal({ physicians, onSave, onClose }) {
  const [form, setForm]           = useState({ ...EMPTY_CLIENT });
  const [errors, setErrors]       = useState({});
  const [saving, setSaving]       = useState(false);
  const [saveMsg, setSaveMsg]     = useState('');

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  }

  function validate() {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim())  e.lastName  = 'Required';
    if (!form.dateOfBirth)      e.dateOfBirth = 'Required';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(form.email)) e.email = 'Invalid email';
    if (form.zip && !/^\d{5}(-\d{4})?$/.test(form.zip)) e.zip = 'Use 12345 or 12345-6789';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSave() {
    if (!validate()) return;
    setSaving(true);
    setSaveMsg('');
    try {
      const result = await window.zignagenixAPI.createPatient(form);
      if (result?.success) {
        onSave({ id: result.id, mrn: result.mrn, name: `${form.firstName} ${form.lastName}`, first_name: form.firstName, last_name: form.lastName });
      } else {
        setSaveMsg(result?.message || 'Failed to create patient');
      }
    } catch (err) {
      setSaveMsg('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999, padding: '1rem',
  };

  return (
    <div style={overlayStyle}>
      <div style={{ background: '#F8F9FA', borderRadius: '16px', boxShadow: '10px 10px 30px rgba(186,199,216,0.6), -10px -10px 30px rgba(255,255,255,0.9)', maxWidth: '680px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem 1.5rem', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <UserPlus size={20} color="#2563eb" />
            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#111827' }}>Register New Client</h2>
          </div>
          <button className="btn-ghost btn-sm" onClick={onClose} style={{ padding: '4px' }}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.5rem' }}>
          {saveMsg && (
            <div style={{ background: '#fee2e2', border: 'none', boxShadow: '3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.85)', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem', color: '#991b1b', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <AlertTriangle size={15} /> {saveMsg}
            </div>
          )}

          {/* Name */}
          <div style={{ marginBottom: '0.5rem', fontWeight: 700, fontSize: '0.7rem', color: '#6C757D', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Identity</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">First Name *</label>
              <input className={`form-input ${errors.firstName ? 'form-input--error' : ''}`}
                value={form.firstName} onChange={e => set('firstName', e.target.value)} placeholder="First name" />
              {errors.firstName && <span className="form-error">{errors.firstName}</span>}
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Last Name *</label>
              <input className={`form-input ${errors.lastName ? 'form-input--error' : ''}`}
                value={form.lastName} onChange={e => set('lastName', e.target.value)} placeholder="Last name" />
              {errors.lastName && <span className="form-error">{errors.lastName}</span>}
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Date of Birth *</label>
              <input type="date" className={`form-input ${errors.dateOfBirth ? 'form-input--error' : ''}`}
                value={form.dateOfBirth} onChange={e => set('dateOfBirth', e.target.value)} />
              {errors.dateOfBirth && <span className="form-error">{errors.dateOfBirth}</span>}
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Gender</label>
              <select className="form-input" value={form.gender} onChange={e => set('gender', e.target.value)}>
                {GENDER_OPTIONS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
            </div>
          </div>

          {/* Contact */}
          <div style={{ marginBottom: '0.5rem', fontWeight: 700, fontSize: '0.7rem', color: '#6C757D', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Contact</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Phone</label>
              <input className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(555) 123-4567" />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Email</label>
              <input type="email" className={`form-input ${errors.email ? 'form-input--error' : ''}`}
                value={form.email} onChange={e => set('email', e.target.value)} placeholder="patient@email.com" />
              {errors.email && <span className="form-error">{errors.email}</span>}
            </div>
            <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
              <label className="form-label">Address</label>
              <input className="form-input" value={form.address} onChange={e => set('address', e.target.value)} placeholder="123 Main St" />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">City</label>
              <input className="form-input" value={form.city} onChange={e => set('city', e.target.value)} placeholder="City" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">State</label>
                <select className="form-input" value={form.state} onChange={e => set('state', e.target.value)}>
                  <option value="">—</option>
                  {US_STATES.filter(Boolean).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">ZIP</label>
                <input className={`form-input ${errors.zip ? 'form-input--error' : ''}`}
                  value={form.zip} onChange={e => set('zip', e.target.value)} placeholder="12345" />
                {errors.zip && <span className="form-error">{errors.zip}</span>}
              </div>
            </div>
          </div>

          {/* Insurance */}
          <div style={{ marginBottom: '0.5rem', fontWeight: 700, fontSize: '0.7rem', color: '#6C757D', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Insurance</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Insurance Provider</label>
              <input className="form-input" value={form.insuranceProvider} onChange={e => set('insuranceProvider', e.target.value)} placeholder="e.g., Blue Cross" />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Member / Policy ID</label>
              <input className="form-input" value={form.insuranceId} onChange={e => set('insuranceId', e.target.value)} placeholder="Policy number" />
            </div>
          </div>

          {/* Primary Physician */}
          {physicians.length > 0 && (
            <>
              <div style={{ marginBottom: '0.5rem', fontWeight: 700, fontSize: '0.7rem', color: '#6C757D', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Care Team</div>
              <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                <label className="form-label">Primary Physician</label>
                <select className="form-input" value={form.primaryPhysicianId} onChange={e => set('primaryPhysicianId', e.target.value)}>
                  <option value="">— Assign later —</option>
                  {physicians.map(p => <option key={p.id} value={p.id}>{p.display_name || p.name}</option>)}
                </select>
              </div>
            </>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '0.75rem', paddingTop: '1rem', borderTop: 'none' }}>
            <button className="btn-primary" onClick={handleSave} disabled={saving} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
              <UserPlus size={15} /> {saving ? 'Registering…' : 'Register Client'}
            </button>
            <button className="btn-secondary" onClick={onClose} style={{ flex: 1 }} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Encounters Component ────────────────────────────────────────────────

const Encounters = ({ user }) => {
  const [encounters, setEncounters]           = useState([]);
  const [patients, setPatients]               = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [selectedEncounterId, setSelectedEncounterId] = useState(null);
  const [selectedEncounter, setSelectedEncounter]     = useState(null);
  const [view, setView]                       = useState('list');
  const [isEditing, setIsEditing]             = useState(false);
  const [isLoading, setIsLoading]             = useState(false);
  const [showSignConfirm, setShowSignConfirm] = useState(false);
  const [providers, setProviders]             = useState([]);
  const [showNewClient, setShowNewClient]     = useState(false);

  const [isWalkin, setIsWalkin] = useState(false);
  const [invoiceMessage, setInvoiceMessage] = useState(null);

  const [formData, setFormData] = useState({
    patientId: '', providerId: '', encounterType: 'office_visit',
    encounterDate: new Date().toISOString().split('T')[0],
    chiefComplaint: '', subjective: '', objective: '',
    assessment: '', plan: '', icd10Codes: '', cptCodes: '',
    snomedCodes: '', hcpcsCodes: '', placeOfService: '11', modifiers: '',
    ndcCodes: '', fhirEncounterClass: 'AMB', status: 'draft',
    // Walk-in person fields
    walkinFirstName: '', walkinLastName: '', walkinDob: '',
    walkinGender: 'U', walkinPhone: '', walkinEmail: '',
  });

  useEffect(() => { loadPatients(); loadProviders(); }, []);

  useEffect(() => {
    if (selectedPatientId) loadEncounters(selectedPatientId);
    else setEncounters([]);
  }, [selectedPatientId]);

  useEffect(() => {
    if (selectedEncounterId) loadEncounterDetail(selectedEncounterId);
  }, [selectedEncounterId]);

  // #65 Phase C2b-2: the encounter detail (read-only view) is this page's
  // master->detail sub-view. Register ONE Back step so global Back returns to the
  // encounter LIST before navigating away — same handler the in-view "Back to
  // List" button uses. Unconditional, above the `view === 'detail'` early return
  // (rules-of-hooks). Label is a LITERAL static string — NEVER a patient name,
  // MRN, encounter date, or any row value (the nav stack persists to
  // sessionStorage; #279 leak class). The editable form is the separate
  // `view === 'create'` branch, deferred to C3.
  useNavStep({
    active: view === 'detail' && !!selectedEncounter,
    label: 'Encounter',
    onBack: () => setView('list'),
  });

  const loadPatients = async () => {
    try {
      const data = await window.zignagenixAPI.getPatients();
      setPatients(Array.isArray(data) ? data : []);
    } catch (err) { console.error('Failed to load patients:', err); }
  };

  const loadProviders = async () => {
    try {
      const data = await window.zignagenixAPI.getUsers();
      setProviders(Array.isArray(data) ? data : []);
    } catch (err) { console.error('Failed to load providers:', err); }
  };

  const loadEncounters = async (patientId) => {
    try {
      setIsLoading(true);
      const data = await window.zignagenixAPI.getEncounters(patientId);
      setEncounters(Array.isArray(data) ? data : []);
      setSelectedEncounterId(null);
      setSelectedEncounter(null);
    } catch (err) { console.error('Failed to load encounters:', err); }
    finally { setIsLoading(false); }
  };

  const loadEncounterDetail = async (encounterId) => {
    try {
      const data = await window.zignagenixAPI.getEncounter(encounterId);
      setSelectedEncounter(data);
      setView('detail');
    } catch (err) { console.error('Failed to load encounter:', err); }
  };

  const handleCreateNew = () => {
    if (!user?.permissions?.includes('encounters:create')) {
      alert('You do not have permission to create encounters'); return;
    }
    setFormData({
      patientId: selectedPatientId || '', providerId: user?.id || '',
      encounterType: 'office_visit',
      encounterDate: new Date().toISOString().split('T')[0],
      chiefComplaint: '', subjective: '', objective: '',
      assessment: '', plan: '', icd10Codes: '', cptCodes: '',
      snomedCodes: '', hcpcsCodes: '', placeOfService: '11', modifiers: '',
      ndcCodes: '', fhirEncounterClass: 'AMB', status: 'draft',
    });
    setIsEditing(false);
    setIsWalkin(false);
    setView('create');
  };

  const handleCreateWalkin = () => {
    if (!user?.permissions?.includes('encounters:create')) {
      alert('You do not have permission to create encounters'); return;
    }
    setFormData({
      patientId: '', providerId: user?.id || '',
      encounterType: 'initial_encounter',
      encounterDate: new Date().toISOString().split('T')[0],
      chiefComplaint: '', subjective: '', objective: '',
      assessment: '', plan: '', icd10Codes: '', cptCodes: '',
      snomedCodes: '', hcpcsCodes: '', placeOfService: '11', modifiers: '',
      ndcCodes: '', fhirEncounterClass: 'AMB', status: 'draft',
      walkinFirstName: '', walkinLastName: '', walkinDob: '',
      walkinGender: 'U', walkinPhone: '', walkinEmail: '',
    });
    setIsEditing(false);
    setIsWalkin(true);
    setView('create');
  };

  const handleEditEncounter = () => {
    if (!user?.permissions?.includes('encounters:update')) {
      alert('You do not have permission to edit encounters'); return;
    }
    if (selectedEncounter?.status === 'signed') { alert('Cannot edit signed encounters'); return; }
    setFormData({
      patientId:      selectedEncounter.patient_id,
      providerId:     selectedEncounter.provider_id,
      encounterType:  selectedEncounter.encounter_type,
      encounterDate:  selectedEncounter.encounter_date,
      chiefComplaint: selectedEncounter.chief_complaint,
      subjective:     selectedEncounter.subjective,
      objective:      selectedEncounter.objective,
      assessment:     selectedEncounter.assessment,
      plan:           selectedEncounter.plan,
      icd10Codes:     selectedEncounter.icd10_codes || '',
      cptCodes:       selectedEncounter.cpt_codes   || '',
      snomedCodes:    selectedEncounter.snomed_codes || '',
      hcpcsCodes:     selectedEncounter.hcpcs_codes || '',
      placeOfService: selectedEncounter.place_of_service || '11',
      modifiers:      selectedEncounter.modifiers || '',
      ndcCodes:       selectedEncounter.ndc_codes || '',
      fhirEncounterClass: selectedEncounter.fhir_encounter_class || 'AMB',
      status:         selectedEncounter.status,
    });
    setIsEditing(true);
  };

  const handleFormChange = (field, value) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  const handleSaveEncounter = async () => {
    // Validate walk-in required fields
    if (isWalkin) {
      if (!formData.walkinFirstName.trim() || !formData.walkinLastName.trim() || !formData.walkinDob) {
        alert('Walk-in encounters require First Name, Last Name, and Date of Birth');
        return;
      }
    } else if (!isEditing && !formData.patientId) {
      alert('Please select a patient');
      return;
    }

    try {
      setIsLoading(true);
      if (isEditing && selectedEncounterId) {
        await window.zignagenixAPI.updateEncounter(selectedEncounterId, formData);
      } else {
        const payload = { ...formData };
        if (isWalkin) {
          payload.isWalkin = true;
        }
        const result = await window.zignagenixAPI.createEncounter(payload);
        if (result?.error) {
          alert(result.message || 'Failed to create encounter');
          return;
        }
        // If walk-in created a new patient, auto-select them
        if (result?.isWalkin && result?.patientId) {
          await loadPatients();
          setSelectedPatientId(String(result.patientId));
        }
      }
      if (selectedPatientId) await loadEncounters(selectedPatientId);
      setView('list');
      setIsEditing(false);
      setIsWalkin(false);
    } catch (err) {
      console.error('Failed to save encounter:', err);
      alert('Failed to save encounter');
    } finally { setIsLoading(false); }
  };

  const handleSignEncounter = async () => {
    if (!user?.permissions?.includes('encounters:sign')) {
      alert('You do not have permission to sign encounters'); return;
    }
    try {
      setIsLoading(true);
      await window.zignagenixAPI.signEncounter(selectedEncounterId);
      await loadEncounterDetail(selectedEncounterId);
      await loadEncounters(selectedPatientId);
      setShowSignConfirm(false);
    } catch (err) {
      console.error('Failed to sign encounter:', err);
      alert('Failed to sign encounter');
    } finally { setIsLoading(false); }
  };

  const handleGenerateInvoice = async () => {
    if (!user?.permissions?.includes('invoices:create')) {
      alert('You do not have permission to generate invoices'); return;
    }
    try {
      setIsLoading(true);
      const result = await window.zignagenixAPI.encounterToInvoice(selectedEncounterId);
      if (result?.success) {
        setInvoiceMessage(`Invoice created successfully (ID: ${result.invoiceId})`);
        setTimeout(() => setInvoiceMessage(null), 5000);
      } else {
        alert('Failed to generate invoice');
      }
    } catch (err) {
      console.error('Failed to generate invoice:', err);
      alert('Failed to generate invoice');
    } finally { setIsLoading(false); }
  };

  // Called after a new client is successfully registered from the modal
  const handleNewClientSaved = async (newPatient) => {
    setShowNewClient(false);
    // Refresh the patients list
    await loadPatients();
    // Auto-select the new patient
    setSelectedPatientId(String(newPatient.id));
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'signed':    return 'status-badge status-badge-signed';
      case 'completed': return 'status-badge status-badge-completed';
      case 'draft':     return 'status-badge status-badge-draft';
      default:          return 'status-badge';
    }
  };

  const getProviderName = (providerId) => {
    const p = providers.find(p => p.id === providerId);
    return p?.display_name || 'Unknown Provider';
  };

  const getPatientName = (patientId) => {
    const p = patients.find(p => p.id === patientId);
    return p ? `${p.last_name}, ${p.first_name}` : 'Unknown Patient';
  };

  const physicians = providers.filter(u => u.role === 'physician' || u.role === 'admin');

  // ── List View ──────────────────────────────────────────────────────────────
  if (view === 'list') {
    const canCreatePatient = user?.permissions?.includes('patients:create');

    return (
      <div className="page">
        {showNewClient && (
          <NewClientModal
            physicians={physicians}
            onSave={handleNewClientSaved}
            onClose={() => setShowNewClient(false)}
          />
        )}

        <div className="page-header-row">
          <div className="page-title-section" style={{ marginBottom: 0 }}>
            <ClipboardList className="page-icon" />
            <h1 className="page-title">Encounters</h1>
          </div>
          <PrintButton pageTitle="Encounters" />
        </div>

        <div className="filter-bar">
          <PatientDropdown
            patients={patients}
            value={selectedPatientId || ''}
            onChange={setSelectedPatientId}
            placeholder="Select a Patient"
            idPrefix="enc-filter"
          />

          {/* Register New Client */}
          {canCreatePatient && (
            <button
              className="btn-primary"
              onClick={() => setShowNewClient(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', whiteSpace: 'nowrap' }}
            >
              <UserPlus size={16} /> Register New Client
            </button>
          )}

          {selectedPatientId && user?.permissions?.includes('encounters:create') && (
            <button className="btn btn-primary" onClick={handleCreateNew}>
              <Plus size={18} /> New Encounter
            </button>
          )}

          {user?.permissions?.includes('encounters:create') && (
            <button
              className="btn-secondary"
              onClick={handleCreateWalkin}
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', whiteSpace: 'nowrap' }}
            >
              <UserRoundPlus size={16} /> Walk-in / Initial Encounter
            </button>
          )}
        </div>

        {/* Empty state */}
        {!selectedPatientId && (
          <div className="empty-state">
            <User size={40} style={{ marginBottom: '0.75rem', opacity: 0.3 }} />
            <p className="empty-state__title">Select a patient above to view their encounters</p>
          </div>
        )}

        {isLoading && <p>Loading encounters…</p>}

        {selectedPatientId && encounters.length === 0 && !isLoading && (
          <div className="empty-state">
            <p className="empty-state__title">No encounters found for this patient</p>
            <p className="empty-state__subtitle">Get started by creating the first encounter record.</p>
            {user?.permissions?.includes('encounters:create') && (
              <button className="btn btn-primary" onClick={handleCreateNew} style={{ marginTop: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                <Plus size={15} /> Create First Encounter
              </button>
            )}
          </div>
        )}

        {selectedPatientId && encounters.length > 0 && (
          <div className="encounters-list">
            {encounters.map(encounter => (
              <div
                key={encounter.id}
                className="encounter-card"
                onClick={() => setSelectedEncounterId(encounter.id)}
              >
                <div className="encounter-card-header">
                  <div>
                    <h3 className="encounter-type">
                      <Stethoscope size={16} className="icon-spacing" />
                      {encounter.encounter_type.replace(/_/g, ' ').toUpperCase()}
                    </h3>
                    <p className="encounter-date">
                      <Calendar size={14} className="icon-spacing-small" />
                      {new Date(encounter.encounter_date).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={getStatusBadgeClass(encounter.status)}>
                    {encounter.status.charAt(0).toUpperCase() + encounter.status.slice(1)}
                  </span>
                </div>
                <p className="encounter-complaint">
                  {encounter.is_walkin && <span style={{ display: 'inline-block', background: '#dbeafe', color: '#1e40af', fontSize: '0.7rem', fontWeight: 600, padding: '2px 6px', borderRadius: '4px', marginRight: '0.4rem', verticalAlign: 'middle' }}>WALK-IN</span>}
                  {encounter.chief_complaint}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Detail View ────────────────────────────────────────────────────────────
  if (view === 'detail' && selectedEncounter) {
    const canEdit = user?.permissions?.includes('encounters:update') && selectedEncounter.status !== 'signed';
    const canSign = user?.permissions?.includes('encounters:sign') && selectedEncounter.status === 'completed';

    return (
      <div className="page">
        <button className="btn btn-ghost margin-bottom-20" onClick={() => setView('list')}>
          <ChevronLeft size={18} /> Back to List
        </button>

        <div className="detail-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 className="page-title">{selectedEncounter.encounter_type.replace(/_/g, ' ').toUpperCase()}</h1>
            <p className="subtitle-detail">
              {getPatientName(selectedEncounter.patient_id)} • {new Date(selectedEncounter.encounter_date).toLocaleDateString()}
            </p>
          </div>
          <div className="flex-row-center gap-12">
            <PrintButton pageTitle="Encounter Detail" subtitle={selectedEncounter.encounter_type.replace(/_/g, ' ')} />
            {selectedEncounter.status === 'signed' && (
              <div className="confirm-banner banner-success">
                <Check size={16} className="icon-spacing inline-display" />
                Signed by {getProviderName(selectedEncounter.provider_id)} on {new Date(selectedEncounter.created_at).toLocaleDateString()}
              </div>
            )}
            {selectedEncounter.status !== 'signed' && (
              <span className={getStatusBadgeClass(selectedEncounter.status)}>
                {selectedEncounter.status.charAt(0).toUpperCase() + selectedEncounter.status.slice(1)}
              </span>
            )}
            {selectedEncounter.cpt_codes && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: '#ecf0f1', borderRadius: '4px', fontSize: '13px', color: '#2E8B98', fontWeight: 500 }}>
                <FileText size={14} />
                CPT Codes Available
              </div>
            )}
          </div>
        </div>

        {showSignConfirm && (
          <div className="confirm-banner banner-warning">
            <div className="flex-row-center gap-12">
              <AlertTriangle size={18} />
              <span>Are you sure you want to sign and lock this encounter? This action cannot be undone.</span>
            </div>
            <div className="flex-row gap-8">
              <button className="btn btn-ghost" onClick={() => setShowSignConfirm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSignEncounter} disabled={isLoading}>
                <Lock size={16} /> Confirm Sign
              </button>
            </div>
          </div>
        )}

        <div className="split-layout">
          <div>
            <h2 className="section-title-large">Chief Complaint</h2>
            <p className="detail-value-block">{selectedEncounter.chief_complaint}</p>

            <h2 className="section-title-large soap-title">SOAP Note</h2>
            {['Subjective', 'Objective', 'Assessment', 'Plan'].map(label => (
              <div key={label} className="soap-section">
                <h3>{label}</h3>
                <p className="soap-text">{selectedEncounter[label.toLowerCase()] || '—'}</p>
              </div>
            ))}
          </div>

          <div>
            <h2 className="section-title-large">Details</h2>
            <div className="detail-value-block-wrapper">
              <div className="detail-item">
                <p className="detail-item-label">Provider</p>
                <p className="detail-item-value">{getProviderName(selectedEncounter.provider_id)}</p>
              </div>
              <div className="detail-item">
                <p className="detail-item-label">Encounter Type</p>
                <p className="detail-item-value">{selectedEncounter.encounter_type.replace(/_/g, ' ')}</p>
              </div>
              <div className="detail-item">
                <p className="detail-item-label">Date</p>
                <p className="detail-item-value">{new Date(selectedEncounter.encounter_date).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="detail-item-label">Status</p>
                <span className={getStatusBadgeClass(selectedEncounter.status)}>
                  {selectedEncounter.status.charAt(0).toUpperCase() + selectedEncounter.status.slice(1)}
                </span>
              </div>
            </div>

            {/* FHIR R4 Encounter Resource — maps to: resourceType: "Encounter", class: fhirEncounterClass,
                reasonCode: [{coding: [{system: "http://hl7.org/fhir/sid/icd-10-cm"}]}],
                serviceType: [{coding: [{system: "http://www.ama-assn.org/go/cpt"}]}] */}

            {selectedEncounter.icd10_codes && (
              <div className="codes-section">
                <h3 className="codes-section-title">ICD-10 Codes</h3>
                <div className="code-block">{selectedEncounter.icd10_codes}</div>
              </div>
            )}
            {selectedEncounter.cpt_codes && (
              <div className="codes-section">
                <h3 className="codes-section-title">CPT Codes</h3>
                <div className="code-block">{selectedEncounter.cpt_codes}</div>
              </div>
            )}

            <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '2px solid #2E8B98' }}>
              <h2 className="section-title-large" style={{ marginBottom: '1rem', color: '#2E8B98' }}>Clinical Coding Standards</h2>

              {selectedEncounter.snomed_codes && (
                <div className="codes-section">
                  <h3 className="codes-section-title">SNOMED CT Codes</h3>
                  <div className="code-block">{selectedEncounter.snomed_codes}</div>
                </div>
              )}

              {selectedEncounter.hcpcs_codes && (
                <div className="codes-section">
                  <h3 className="codes-section-title">HCPCS Codes</h3>
                  <div className="code-block">{selectedEncounter.hcpcs_codes}</div>
                </div>
              )}

              {selectedEncounter.ndc_codes && (
                <div className="codes-section">
                  <h3 className="codes-section-title">NDC Codes</h3>
                  <div className="code-block">{selectedEncounter.ndc_codes}</div>
                </div>
              )}

              {(selectedEncounter.place_of_service || selectedEncounter.modifiers) && (
                <div className="codes-section">
                  <h3 className="codes-section-title">Modifiers &amp; Place of Service</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    {selectedEncounter.place_of_service && (
                      <div className="detail-item">
                        <p className="detail-item-label">Place of Service</p>
                        <p className="detail-item-value">{selectedEncounter.place_of_service}</p>
                      </div>
                    )}
                    {selectedEncounter.modifiers && (
                      <div className="detail-item">
                        <p className="detail-item-label">CPT/HCPCS Modifiers</p>
                        <p className="detail-item-value">{selectedEncounter.modifiers}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedEncounter.fhir_encounter_class && (
                <div className="codes-section">
                  <h3 className="codes-section-title">FHIR Encounter Class</h3>
                  <div className="code-block">{selectedEncounter.fhir_encounter_class}</div>
                </div>
              )}

              <div style={{
                marginTop: '1.5rem',
                padding: '1rem',
                background: '#f0f9fb',
                border: 'none',
                boxShadow: '3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.85)',
                borderRadius: '6px',
                fontSize: '0.85rem',
                color: '#1a5f6f'
              }}>
                <strong>Interoperability Info:</strong> HL7 FHIR R4 Encounter | USCDI v3 Compliant | CMS-1500 Ready
              </div>
            </div>

            <div className="button-column-group">
              {canEdit && (
                <button className="btn btn-primary" onClick={handleEditEncounter}>
                  <Edit3 size={16} /> Edit Encounter
                </button>
              )}
              {canSign && !showSignConfirm && (
                <button className="btn btn-primary" onClick={() => setShowSignConfirm(true)}>
                  <Lock size={16} /> Sign &amp; Lock
                </button>
              )}
              {selectedEncounter.status === 'signed' && (
                <button className="btn btn-ghost" disabled>
                  <Lock size={16} /> Locked
                </button>
              )}
              {selectedEncounter.status === 'signed' && selectedEncounter.cpt_codes && (
                <button
                  className="btn"
                  onClick={handleGenerateInvoice}
                  disabled={isLoading}
                  style={{
                    background: 'linear-gradient(145deg, #2E8B98, #007a96)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: 13,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    opacity: isLoading ? 0.6 : 1,
                  }}
                >
                  <FileText size={16} /> Generate Invoice
                </button>
              )}
            </div>
            {invoiceMessage && (
              <div style={{
                marginTop: '1rem',
                padding: '10px 14px',
                background: '#d4edda',
                color: '#155724',
                border: 'none',
                boxShadow: '3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.85)',
                borderRadius: '4px',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <Check size={16} />
                {invoiceMessage}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Create / Edit View ─────────────────────────────────────────────────────
  if (view === 'create') {
    return (
      <div className="page">
        <button className="btn btn-ghost margin-bottom-20" onClick={() => { setView('list'); setIsWalkin(false); }}>
          <ChevronLeft size={18} /> Back to List
        </button>

        <div style={{ marginBottom: '1.5rem' }}>
          <h1 className="page-title page-title-spacious" style={{ margin: 0 }}>
            {isEditing ? 'Edit Encounter' : isWalkin ? 'Walk-in / Initial Encounter' : 'New Encounter'}
          </h1>
          {isWalkin && (
            <p style={{ color: '#6C757D', fontSize: '0.9rem', marginTop: '0.25rem' }}>
              Create an encounter for a person not yet in the system. A patient record will be created automatically.
            </p>
          )}
        </div>

        {showNewClient && (
          <NewClientModal
            physicians={physicians}
            onSave={async (newPatient) => {
              setShowNewClient(false);
              await loadPatients();
              handleFormChange('patientId', String(newPatient.id));
            }}
            onClose={() => setShowNewClient(false)}
          />
        )}

        <div className="form-container">
          <form className="form-grid">

            {/* Walk-in: inline person fields */}
            {isWalkin && (
              <>
                <div style={{ gridColumn: '1 / -1', marginBottom: '0.25rem' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.7rem', color: '#6C757D', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>Person Information</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">First Name *</label>
                      <input className="form-input" value={formData.walkinFirstName}
                        onChange={e => handleFormChange('walkinFirstName', e.target.value)}
                        placeholder="First name" required />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Last Name *</label>
                      <input className="form-input" value={formData.walkinLastName}
                        onChange={e => handleFormChange('walkinLastName', e.target.value)}
                        placeholder="Last name" required />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Date of Birth *</label>
                      <input type="date" className="form-input" value={formData.walkinDob}
                        onChange={e => handleFormChange('walkinDob', e.target.value)} required />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Gender</label>
                      <select className="form-input" value={formData.walkinGender}
                        onChange={e => handleFormChange('walkinGender', e.target.value)}>
                        {GENDER_OPTIONS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                      </select>
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Phone</label>
                      <input className="form-input" value={formData.walkinPhone}
                        onChange={e => handleFormChange('walkinPhone', e.target.value)}
                        placeholder="(555) 123-4567" />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Email</label>
                      <input type="email" className="form-input" value={formData.walkinEmail}
                        onChange={e => handleFormChange('walkinEmail', e.target.value)}
                        placeholder="person@email.com" />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Existing patient selector (hidden for walk-ins) */}
            {!isWalkin && (
              <div className="form-group">
                <label className="form-label">Patient</label>
                <PatientDropdown
                  patients={patients}
                  value={formData.patientId}
                  onChange={val => handleFormChange('patientId', val)}
                  placeholder="Select a patient"
                  disabled={isEditing}
                  idPrefix="enc-form"
                />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Encounter Type</label>
              <select className="form-input" value={formData.encounterType}
                onChange={e => handleFormChange('encounterType', e.target.value)} required>
                <option value="initial_encounter">Initial Encounter</option>
                <option value="office_visit">Office Visit</option>
                <option value="telehealth">Telehealth</option>
                <option value="emergency">Emergency</option>
                <option value="inpatient">Inpatient</option>
                <option value="lab_review">Lab Review</option>
                <option value="follow_up">Follow-up</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Encounter Date</label>
              <input type="date" className="form-input" value={formData.encounterDate}
                onChange={e => handleFormChange('encounterDate', e.target.value)} required />
            </div>

            <div className="form-group">
              <label className="form-label">Chief Complaint</label>
              <input type="text" className="form-input" value={formData.chiefComplaint}
                onChange={e => handleFormChange('chiefComplaint', e.target.value)}
                placeholder="Patient's primary concern" required />
            </div>

            {['subjective', 'objective', 'assessment', 'plan'].map(field => (
              <div key={field} className="form-group">
                <label className="form-label">{field.charAt(0).toUpperCase() + field.slice(1)}</label>
                <textarea className="form-input form-input-monospace" value={formData[field]}
                  onChange={e => handleFormChange(field, e.target.value)}
                  placeholder={
                    field === 'subjective'  ? "Patient's history and symptoms" :
                    field === 'objective'   ? 'Physical exam findings, vital signs, lab results' :
                    field === 'assessment'  ? 'Clinical assessment and diagnoses' :
                    'Treatment plan and follow-up'
                  }
                  rows={4} />
              </div>
            ))}

            <div className="form-group">
              <label className="form-label">ICD-10 Codes</label>
              <input type="text" className="form-input" value={formData.icd10Codes}
                onChange={e => handleFormChange('icd10Codes', e.target.value)}
                placeholder="e.g., I10, E11.9 (comma-separated)" />
            </div>

            <div className="form-group">
              <label className="form-label">CPT Codes</label>
              <input type="text" className="form-input" value={formData.cptCodes}
                onChange={e => handleFormChange('cptCodes', e.target.value)}
                placeholder="e.g., 99213, 99214 (comma-separated)" />
            </div>

            {/* Clinical Coding Standards Section */}
            <div style={{ gridColumn: '1 / -1', marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '2px solid #2E8B98' }}>
              <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: 600, color: '#2E8B98' }}>Clinical Coding Standards</h3>
            </div>

            <div className="form-group">
              <label className="form-label">SNOMED CT Codes</label>
              <input type="text" className="form-input" value={formData.snomedCodes}
                onChange={e => handleFormChange('snomedCodes', e.target.value)}
                placeholder="e.g., 73211009 (Diabetes mellitus)" />
            </div>

            <div className="form-group">
              <label className="form-label">HCPCS Codes</label>
              <input type="text" className="form-input" value={formData.hcpcsCodes}
                onChange={e => handleFormChange('hcpcsCodes', e.target.value)}
                placeholder="e.g., J1100 (Dexamethasone injection)" />
            </div>

            <div className="form-group">
              <label className="form-label">NDC Codes</label>
              <input type="text" className="form-input" value={formData.ndcCodes}
                onChange={e => handleFormChange('ndcCodes', e.target.value)}
                placeholder="e.g., 0093-7517-01 (comma-separated)" />
            </div>

            <div className="form-group">
              <label className="form-label">Modifiers</label>
              <input type="text" className="form-input" value={formData.modifiers}
                onChange={e => handleFormChange('modifiers', e.target.value)}
                placeholder="e.g., 25, GT, 59 (comma-separated)" />
            </div>

            <div className="form-group">
              <label className="form-label">Place of Service</label>
              <select className="form-input" value={formData.placeOfService}
                onChange={e => handleFormChange('placeOfService', e.target.value)}>
                <option value="11">11 - Office</option>
                <option value="21">21 - Inpatient Hospital</option>
                <option value="22">22 - Outpatient Hospital</option>
                <option value="23">23 - Emergency Room</option>
                <option value="02">02 - Telehealth</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">FHIR Encounter Class</label>
              <select className="form-input" value={formData.fhirEncounterClass}
                onChange={e => handleFormChange('fhirEncounterClass', e.target.value)}>
                <option value="AMB">AMB - Ambulatory</option>
                <option value="EMER">EMER - Emergency</option>
                <option value="IMP">IMP - Inpatient</option>
                <option value="SS">SS - Short Stay</option>
                <option value="HH">HH - Home Health</option>
                <option value="VR">VR - Virtual</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-input" value={formData.status}
                onChange={e => handleFormChange('status', e.target.value)}>
                <option value="draft">Draft</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            <div className="patient-form-actions">
              <button type="button" className="btn btn-ghost" onClick={() => { setView('list'); setIsWalkin(false); }} disabled={isLoading}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSaveEncounter} disabled={isLoading}>
                <Save size={16} /> {isEditing ? 'Update' : 'Create'} Encounter
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return null;
};

export default Encounters;
